import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { format } from "date-fns";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TiptapLink from "@tiptap/extension-link";
import Highlight from "@tiptap/extension-highlight";
import {
  Plus,
  Send,
  Edit,
  Trash2,
  Eye,
  Users,
  Mail,
  MailOpen,
  MousePointerClick,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Bold,
  Italic,
  Strikethrough,
  List,
  ListOrdered,
  Link as LinkIcon,
  Highlighter,
  Undo,
  Redo,
  FileText,
} from "lucide-react";

// ── Toolbar ──
const EditorToolbar = ({ editor }) => {
  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const buttons = [
    { id: "bold", action: () => editor.chain().focus().toggleBold().run(), active: "bold", icon: Bold },
    { id: "italic", action: () => editor.chain().focus().toggleItalic().run(), active: "italic", icon: Italic },
    { id: "strike", action: () => editor.chain().focus().toggleStrike().run(), active: "strike", icon: Strikethrough },
    { id: "highlight", action: () => editor.chain().focus().toggleHighlight().run(), active: "highlight", icon: Highlighter },
    { id: "div1", divider: true },
    { id: "bullet", action: () => editor.chain().focus().toggleBulletList().run(), active: "bulletList", icon: List },
    { id: "ordered", action: () => editor.chain().focus().toggleOrderedList().run(), active: "orderedList", icon: ListOrdered },
    { id: "div2", divider: true },
    { id: "link", action: setLink, active: "link", icon: LinkIcon },
    { id: "div3", divider: true },
    { id: "undo", action: () => editor.chain().focus().undo().run(), disabled: !editor.can().undo(), icon: Undo },
    { id: "redo", action: () => editor.chain().focus().redo().run(), disabled: !editor.can().redo(), icon: Redo },
  ];

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b border-cfc-border bg-cfc-cream">
      {buttons.map((btn) =>
        btn.divider ? (
          <div key={btn.id} className="w-px h-6 bg-cfc-border mx-1" />
        ) : (
          <Button
            key={btn.id}
            type="button"
            variant="ghost"
            size="sm"
            onClick={btn.action}
            disabled={btn.disabled}
            className={btn.active && editor.isActive(btn.active) ? "bg-cfc-primary text-white" : ""}
          >
            <btn.icon size={16} />
          </Button>
        )
      )}
    </div>
  );
};

// ── Status badge ──
const StatusBadge = ({ status }) => {
  const map = {
    draft: { label: "Draft", cls: "bg-gray-100 text-gray-700" },
    sending: { label: "Sending...", cls: "bg-yellow-100 text-yellow-700" },
    sent: { label: "Sent", cls: "bg-green-100 text-green-700" },
    failed: { label: "Failed", cls: "bg-red-100 text-red-700" },
  };
  const s = map[status] || map.draft;
  return (
    <span data-testid={`campaign-status-${status}`} className={`inline-flex items-center px-2.5 py-0.5 rounded-sm text-xs font-medium ${s.cls}`}>
      {s.label}
    </span>
  );
};

import EmailTemplates from "./EmailTemplates";

// ── Audience label helper ──
const audienceLabel = (audience) => {
  if (!audience) return "All Leads";
  if (audience.selected_lead_ids?.length) return `${audience.selected_lead_ids.length} Selected Leads`;
  const parts = [];
  if (audience.lead_type && audience.lead_type !== "all") parts.push(audience.lead_type === "player" ? "Players" : "Coaches");
  if (audience.status && audience.status !== "all") parts.push(audience.status.replace("_", " "));
  return parts.length > 0 ? parts.join(" / ") : "All Leads";
};

// ── Main Component ──
const CampaignManager = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showComposer, setShowComposer] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [sendTarget, setSendTarget] = useState(null);
  const [previewCount, setPreviewCount] = useState(null);
  const [sending, setSending] = useState(false);
  const [tryouts, setTryouts] = useState([]);
  const [statsCache, setStatsCache] = useState({});
  const [showStats, setShowStats] = useState(null);

  // Form state
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [audienceType, setAudienceType] = useState("all");
  const [audienceStatus, setAudienceStatus] = useState("all");
  const [audienceTryout, setAudienceTryout] = useState("all");
  const [recipientCount, setRecipientCount] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectionMode, setSelectionMode] = useState("filter"); // "filter" or "manual"
  const [allLeads, setAllLeads] = useState([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [leadSearch, setLeadSearch] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit,
      TiptapLink.configure({ openOnClick: false }),
      Highlight,
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none p-4 min-h-[200px] focus:outline-none",
      },
    },
  });

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/campaigns`, { withCredentials: true });
      setCampaigns(res.data);
    } catch (error) {
      console.error("Failed to fetch campaigns:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch tryouts for audience filter
  const fetchTryouts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tryouts`, { withCredentials: true });
      setTryouts(res.data);
    } catch (error) {
      console.error("Failed to fetch tryouts:", error);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
    fetchTryouts();
    // Fetch all leads for manual selection
    const fetchLeads = async () => {
      try {
        const res = await axios.get(`${API}/leads`, { withCredentials: true });
        setAllLeads(res.data.filter(l => l.email));
      } catch (e) { /* ignore */ }
    };
    fetchLeads();
  }, [fetchCampaigns, fetchTryouts]);

  // Fetch tracking stats for sent campaigns
  useEffect(() => {
    const fetchAllStats = async () => {
      const sentCampaigns = campaigns.filter(c => c.status === "sent" || c.status === "sending");
      const newCache = { ...statsCache };
      for (const c of sentCampaigns) {
        try {
          const res = await axios.get(`${API}/campaigns/${c.campaign_id}/stats`, { withCredentials: true });
          newCache[c.campaign_id] = res.data;
        } catch (error) {
          console.error("Failed to fetch campaign stats:", error);
        }
      }
      if (sentCampaigns.length > 0) setStatsCache(newCache);
    };
    if (campaigns.length > 0) fetchAllStats();
  }, [campaigns]);

  // Live recipient count
  useEffect(() => {
    const fetchCount = async () => {
      if (selectionMode === "manual") {
        setRecipientCount(selectedLeadIds.length);
        return;
      }
      try {
        const audience = buildAudience();
        const res = await axios.post(`${API}/campaigns/preview-audience`, { audience }, { withCredentials: true });
        setRecipientCount(res.data.total);
      } catch (error) {
        console.error("Failed to preview audience:", error);
        setRecipientCount(null);
      }
    };
    if (showComposer) fetchCount();
  }, [audienceType, audienceStatus, audienceTryout, showComposer, selectionMode, selectedLeadIds]);

  const buildAudience = () => {
    if (selectionMode === "manual") {
      return { selected_lead_ids: selectedLeadIds };
    }
    return {
      lead_type: audienceType,
      status: audienceStatus,
      tryout_id: audienceTryout,
    };
  };

  const resetForm = () => {
    setName("");
    setSubject("");
    setAudienceType("all");
    setAudienceStatus("all");
    setAudienceTryout("all");
    setRecipientCount(null);
    setEditingCampaign(null);
    setSelectionMode("filter");
    setSelectedLeadIds([]);
    setLeadSearch("");
    if (editor) editor.commands.setContent("");
  };

  const openComposer = (campaign = null) => {
    if (campaign) {
      setEditingCampaign(campaign);
      setName(campaign.name);
      setSubject(campaign.subject);
      const aud = campaign.audience || {};
      if (aud.selected_lead_ids?.length) {
        setSelectionMode("manual");
        setSelectedLeadIds(aud.selected_lead_ids);
      } else {
        setSelectionMode("filter");
        setAudienceType(aud.lead_type || "all");
        setAudienceStatus(aud.status || "all");
        setAudienceTryout(aud.tryout_id || "all");
      }
      if (editor) editor.commands.setContent(campaign.body_html || "");
    } else {
      resetForm();
    }
    setShowComposer(true);
  };

  const closeComposer = () => {
    setShowComposer(false);
    resetForm();
  };

  // Save draft
  const saveDraft = async () => {
    if (!name.trim() || !subject.trim()) {
      toast.error("Name and subject are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        subject: subject.trim(),
        body_html: editor?.getHTML() || "",
        audience: buildAudience(),
      };
      if (editingCampaign) {
        await axios.put(`${API}/campaigns/${editingCampaign.campaign_id}`, payload, { withCredentials: true });
        toast.success("Campaign updated");
      } else {
        await axios.post(`${API}/campaigns`, payload, { withCredentials: true });
        toast.success("Campaign draft saved");
      }
      closeComposer();
      fetchCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save campaign");
    } finally {
      setSaving(false);
    }
  };

  // Send flow
  const openSendConfirm = async (campaign) => {
    setSendTarget(campaign);
    setPreviewCount(null);
    setShowSendConfirm(true);
    try {
      const res = await axios.post(`${API}/campaigns/${campaign.campaign_id}/preview`, {}, { withCredentials: true });
      setPreviewCount(res.data.total);
    } catch (error) {
      console.error("Failed to preview campaign:", error);
      setPreviewCount(0);
    }
  };

  const confirmSend = async () => {
    if (!sendTarget) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/campaigns/${sendTarget.campaign_id}/send`, {}, { withCredentials: true });
      toast.success(res.data.message);
      setShowSendConfirm(false);
      setSendTarget(null);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send campaign");
    } finally {
      setSending(false);
    }
  };

  // Delete
  const deleteCampaign = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    try {
      await axios.delete(`${API}/campaigns/${id}`, { withCredentials: true });
      toast.success("Campaign deleted");
      fetchCampaigns();
    } catch (error) {
      console.error("Failed to delete campaign:", error);
      toast.error("Failed to delete");
    }
  };

  // ── Render ──
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="campaigns-loading">
        <Loader2 className="animate-spin text-cfc-primary" size={32} />
      </div>
    );
  }

  return (
    <div data-testid="campaign-manager">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-cfc-primary">Email Campaigns</h1>
          <p className="text-sm text-cfc-text-muted mt-1">Compose and send bulk emails to your leads</p>
        </div>
        <Button onClick={() => openComposer()} className="bg-cfc-primary hover:bg-cfc-primary/90" data-testid="new-campaign-btn">
          <Plus size={18} className="mr-2" /> New Campaign
        </Button>
      </div>

      {/* Campaign List */}
      {campaigns.length === 0 ? (
        <Card className="border-cfc-border" data-testid="campaigns-empty">
          <CardContent className="flex flex-col items-center justify-center py-16 text-cfc-text-muted">
            <Mail size={48} className="mb-4 opacity-40" />
            <p className="text-lg font-medium mb-1">No campaigns yet</p>
            <p className="text-sm mb-6">Create your first email campaign to reach your leads</p>
            <Button onClick={() => openComposer()} variant="outline" data-testid="empty-new-campaign-btn">
              <Plus size={16} className="mr-2" /> Create Campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-cfc-border" data-testid="campaigns-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead>Audience</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Stats</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.map((c) => (
                <TableRow key={c.campaign_id} data-testid={`campaign-row-${c.campaign_id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-cfc-primary">{c.name}</p>
                      <p className="text-xs text-cfc-text-muted">{c.subject}</p>
                      <p className="text-xs text-cfc-text-muted mt-0.5">
                        {c.sent_at ? `Sent ${format(new Date(c.sent_at), "MMM d, yyyy h:mm a")}` : `Created ${format(new Date(c.created_at), "MMM d, yyyy")}`}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{audienceLabel(c.audience)}</span>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={c.status} />
                  </TableCell>
                  <TableCell>
                    {c.status !== "draft" ? (
                      <div className="text-xs space-y-0.5">
                        <div className="flex items-center gap-1">
                          <CheckCircle2 size={12} className="text-green-600" />
                          <span>{c.stats?.sent || 0} sent</span>
                        </div>
                        {statsCache[c.campaign_id] ? (
                          <>
                            <div className="flex items-center gap-1">
                              <MailOpen size={12} className="text-blue-500" />
                              <span>{statsCache[c.campaign_id].open_rate}% opened</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <MousePointerClick size={12} className="text-purple-500" />
                              <span>{statsCache[c.campaign_id].click_rate}% clicked</span>
                            </div>
                          </>
                        ) : c.status === "sending" ? (
                          <div className="flex items-center gap-1 text-yellow-600">
                            <Loader2 size={12} className="animate-spin" />
                            <span>In progress</span>
                          </div>
                        ) : null}
                        {c.stats?.failed > 0 && (
                          <div className="flex items-center gap-1">
                            <AlertCircle size={12} className="text-red-500" />
                            <span>{c.stats.failed} failed</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-cfc-text-muted">--</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {c.status === "draft" && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => openComposer(c)} data-testid={`edit-campaign-${c.campaign_id}`}>
                            <Edit size={15} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openSendConfirm(c)} className="text-cfc-primary" data-testid={`send-campaign-${c.campaign_id}`}>
                            <Send size={15} />
                          </Button>
                        </>
                      )}
                      {c.status === "sent" && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => setShowStats(c.campaign_id)} className="text-blue-500" data-testid={`stats-campaign-${c.campaign_id}`}>
                            <Eye size={15} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openSendConfirm(c)} className="text-cfc-primary" data-testid={`resend-campaign-${c.campaign_id}`}>
                            <Send size={15} />
                          </Button>
                        </>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => deleteCampaign(c.campaign_id)} className="text-red-500 hover:text-red-600" data-testid={`delete-campaign-${c.campaign_id}`}>
                        <Trash2 size={15} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Composer Dialog */}
      <Dialog open={showComposer} onOpenChange={(open) => { if (!open) closeComposer(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="campaign-composer">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-cfc-primary">
              {editingCampaign ? "Edit Campaign" : "New Campaign"}
            </DialogTitle>
            <p className="text-sm text-cfc-text-muted">Compose your email campaign and select your audience</p>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            {/* Name */}
            <div>
              <Label>Campaign Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Spring Tryouts Announcement"
                data-testid="campaign-name-input"
              />
            </div>

            {/* Subject */}
            <div>
              <Label>Email Subject Line</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. You're invited to spring tryouts!"
                data-testid="campaign-subject-input"
              />
            </div>

            {/* Audience Filters */}
            <div>
              <Label className="mb-2 block">Audience</Label>
              <div className="flex gap-2 mb-3">
                <Button type="button" variant={selectionMode === "filter" ? "default" : "outline"} size="sm"
                  className={selectionMode === "filter" ? "bg-cfc-primary" : ""}
                  onClick={() => setSelectionMode("filter")} data-testid="audience-mode-filter">
                  Filter by Criteria
                </Button>
                <Button type="button" variant={selectionMode === "manual" ? "default" : "outline"} size="sm"
                  className={selectionMode === "manual" ? "bg-cfc-primary" : ""}
                  onClick={() => setSelectionMode("manual")} data-testid="audience-mode-manual">
                  Select Specific Leads
                </Button>
              </div>

              {selectionMode === "filter" ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-cfc-text-muted">Lead Type</Label>
                  <Select value={audienceType} onValueChange={setAudienceType}>
                    <SelectTrigger data-testid="audience-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Leads</SelectItem>
                      <SelectItem value="player">Players Only</SelectItem>
                      <SelectItem value="coach">Coaches Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-cfc-text-muted">Status</Label>
                  <Select value={audienceStatus} onValueChange={setAudienceStatus}>
                    <SelectTrigger data-testid="audience-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any Status</SelectItem>
                      <SelectItem value="new_lead">New Lead</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="trial_scheduled">Trial Scheduled</SelectItem>
                      <SelectItem value="joined">Joined</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-cfc-text-muted">Tryout</Label>
                  <Select value={audienceTryout} onValueChange={setAudienceTryout}>
                    <SelectTrigger data-testid="audience-tryout-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any Tryout</SelectItem>
                      {tryouts.map((t) => (
                        <SelectItem key={t.tryout_id} value={t.tryout_id}>
                          {t.title || t.age_group} — {t.date}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              ) : (
              <div data-testid="manual-lead-selection">
                <Input
                  placeholder="Search leads by name or email..."
                  value={leadSearch}
                  onChange={(e) => setLeadSearch(e.target.value)}
                  className="mb-2 text-sm"
                  data-testid="lead-search-input"
                />
                <div className="border border-cfc-border rounded max-h-48 overflow-y-auto">
                  {allLeads
                    .filter(l => {
                      if (!leadSearch) return true;
                      const q = leadSearch.toLowerCase();
                      return (l.name || "").toLowerCase().includes(q) || (l.email || "").toLowerCase().includes(q);
                    })
                    .map((lead) => (
                      <label key={lead.lead_id} className="flex items-center gap-3 px-3 py-2 hover:bg-cfc-cream cursor-pointer border-b border-cfc-border/50 last:border-0">
                        <input
                          type="checkbox"
                          checked={selectedLeadIds.includes(lead.lead_id)}
                          onChange={() => setSelectedLeadIds(prev => prev.includes(lead.lead_id) ? prev.filter(id => id !== lead.lead_id) : [...prev, lead.lead_id])}
                          className="rounded accent-cfc-primary"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-cfc-primary truncate">{lead.name}</p>
                          <p className="text-[11px] text-cfc-text-muted truncate">{lead.email}{lead.source && lead.source !== "commonwealth_fc" ? ` • ${lead.source.replace(/_/g, " ")}` : ""}</p>
                        </div>
                      </label>
                    ))}
                </div>
                <div className="flex justify-between mt-1.5">
                  <span className="text-[11px] text-cfc-text-muted">{selectedLeadIds.length} selected</span>
                  <div className="flex gap-3">
                    <button type="button" className="text-[11px] text-cfc-primary hover:underline" onClick={() => setSelectedLeadIds(allLeads.filter(l => !leadSearch || (l.name||"").toLowerCase().includes(leadSearch.toLowerCase()) || (l.email||"").toLowerCase().includes(leadSearch.toLowerCase())).map(l => l.lead_id))}>
                      Select All
                    </button>
                    <button type="button" className="text-[11px] text-cfc-text-muted hover:underline" onClick={() => setSelectedLeadIds([])}>
                      Clear
                    </button>
                  </div>
                </div>
              </div>
              )}
              {recipientCount !== null && (
                <div className="mt-2 flex items-center gap-2 text-sm" data-testid="recipient-count">
                  <Users size={14} className="text-cfc-primary" />
                  <span className="font-medium text-cfc-primary">{recipientCount}</span>
                  <span className="text-cfc-text-muted">recipients match this audience</span>
                </div>
              )}
            </div>

            {/* Email Body Editor */}
            <div>
              <Label className="mb-2 block">Email Body</Label>
              <div className="border border-cfc-border rounded-sm overflow-hidden" data-testid="campaign-editor">
                <EditorToolbar editor={editor} />
                <EditorContent editor={editor} />
              </div>
            </div>
          </div>

          <DialogFooter className="mt-6 gap-2">
            <DialogClose asChild>
              <Button variant="outline" data-testid="cancel-campaign-btn">Cancel</Button>
            </DialogClose>
            <Button onClick={saveDraft} disabled={saving} className="bg-cfc-primary hover:bg-cfc-primary/90" data-testid="save-campaign-btn">
              {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <FileText size={16} className="mr-2" />}
              {editingCampaign ? "Update Draft" : "Save Draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Confirmation Dialog */}
      <Dialog open={showSendConfirm} onOpenChange={(open) => { if (!open) { setShowSendConfirm(false); setSendTarget(null); } }}>
        <DialogContent className="max-w-md" data-testid="send-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-cfc-primary">Confirm Send</DialogTitle>
            <p className="text-sm text-cfc-text-muted">Review and confirm your campaign delivery</p>
          </DialogHeader>
          {sendTarget && (
            <div className="space-y-4 mt-2">
              <div className="bg-cfc-cream rounded-sm p-4">
                <p className="font-medium text-cfc-primary">{sendTarget.name}</p>
                <p className="text-sm text-cfc-text-muted mt-1">Subject: {sendTarget.subject}</p>
                <p className="text-sm text-cfc-text-muted">Audience: {audienceLabel(sendTarget.audience)}</p>
              </div>
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-sm">
                <Mail size={20} className="text-blue-600" />
                {previewCount === null ? (
                  <span className="text-sm text-blue-700">Calculating recipients...</span>
                ) : (
                  <span className="text-sm text-blue-700">
                    This will send <strong>{previewCount}</strong> email{previewCount !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
              {previewCount === 0 && (
                <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 p-3 rounded-sm">
                  <AlertCircle size={16} />
                  No recipients match this audience. Adjust your filters.
                </div>
              )}
            </div>
          )}
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => { setShowSendConfirm(false); setSendTarget(null); }} data-testid="cancel-send-btn">
              Cancel
            </Button>
            <Button
              onClick={confirmSend}
              disabled={sending || previewCount === null || previewCount === 0}
              className="bg-cfc-primary hover:bg-cfc-primary/90"
              data-testid="confirm-send-btn"
            >
              {sending ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={16} className="mr-2" />}
              {sending ? "Sending..." : "Send Now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Stats Detail Dialog — with recipient list */}
      <Dialog open={!!showStats} onOpenChange={(open) => { if (!open) setShowStats(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="stats-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-cfc-primary">Campaign Details</DialogTitle>
            <p className="text-sm text-cfc-text-muted">Delivery analytics and recipient list</p>
          </DialogHeader>
          {showStats && (
            <CampaignRecipientView campaignId={showStats} statsCache={statsCache} />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStats(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── Campaign Recipient View ──
const CampaignRecipientView = ({ campaignId, statsCache }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/campaigns/${campaignId}/recipients`, { withCredentials: true });
        setData(res.data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    fetch();
  }, [campaignId]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-cfc-primary" size={24} /></div>;
  if (!data) return <p className="text-sm text-cfc-text-muted text-center py-4">Failed to load recipient data</p>;

  const stats = statsCache[campaignId];
  const filtered = (data.recipients || []).filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.name || "").toLowerCase().includes(q) || (r.email || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4 mt-2" data-testid="campaign-recipients-view">
      {/* Stats summary */}
      {stats && (
        <div className="grid grid-cols-4 gap-2">
          <div className="bg-green-50 rounded-sm p-3 text-center">
            <p className="text-xl font-bold text-green-700">{stats.total_sent}</p>
            <p className="text-[10px] text-green-600 font-medium">Sent</p>
          </div>
          <div className="bg-blue-50 rounded-sm p-3 text-center">
            <p className="text-xl font-bold text-blue-700">{stats.open_rate}%</p>
            <p className="text-[10px] text-blue-600 font-medium">Opened</p>
          </div>
          <div className="bg-purple-50 rounded-sm p-3 text-center">
            <p className="text-xl font-bold text-purple-700">{stats.click_rate}%</p>
            <p className="text-[10px] text-purple-600 font-medium">Clicked</p>
          </div>
          <div className="bg-red-50 rounded-sm p-3 text-center">
            <p className="text-xl font-bold text-red-700">{data.stats?.failed || 0}</p>
            <p className="text-[10px] text-red-600 font-medium">Failed</p>
          </div>
        </div>
      )}

      <div className="text-sm">
        <p className="font-medium text-cfc-primary">{data.campaign_name}</p>
        <p className="text-cfc-text-muted text-xs">Subject: {data.subject}</p>
        {data.sent_at && <p className="text-cfc-text-muted text-xs">Sent: {format(new Date(data.sent_at), "MMM d, yyyy h:mm a")}</p>}
      </div>

      {/* Recipient list */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold text-cfc-text-muted uppercase">Recipients ({data.recipients?.length || 0})</span>
          <Input
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-48 h-7 text-xs"
            data-testid="campaign-recipients-search"
          />
        </div>
        <div className="border border-cfc-border rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Name</TableHead>
                <TableHead className="text-xs">Email</TableHead>
                <TableHead className="text-xs text-center">Status</TableHead>
                <TableHead className="text-xs text-center">Opened</TableHead>
                <TableHead className="text-xs text-center">Clicked</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-xs text-cfc-text-muted py-4">No recipients found</TableCell></TableRow>
              ) : (
                filtered.map((r, i) => (
                  <TableRow key={r.lead_id || i} data-testid={`recipient-row-${r.lead_id || i}`}>
                    <TableCell className="text-sm font-medium text-cfc-primary py-1.5">{r.name || "—"}</TableCell>
                    <TableCell className="text-xs text-cfc-text-muted py-1.5">{r.email || "—"}</TableCell>
                    <TableCell className="text-center py-1.5">
                      {r.status === "sent" ? (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-green-600"><CheckCircle2 size={11} /> Sent</span>
                      ) : r.status === "failed" ? (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-red-600"><AlertCircle size={11} /> Failed</span>
                      ) : (
                        <span className="text-[10px] text-cfc-text-muted">{r.status || "—"}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center py-1.5">
                      {r.opened ? <MailOpen size={14} className="mx-auto text-blue-500" /> : <span className="text-[10px] text-gray-300">—</span>}
                    </TableCell>
                    <TableCell className="text-center py-1.5">
                      {r.clicked ? <MousePointerClick size={14} className="mx-auto text-purple-500" /> : <span className="text-[10px] text-gray-300">—</span>}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

const CampaignManagerWithTemplates = () => {
  const [activeTab, setActiveTab] = useState("campaigns");

  return (
    <div data-testid="campaign-manager-wrapper">
      <div className="flex items-center gap-1 mb-6 border-b border-cfc-border">
        {[
          { id: "campaigns", label: "Campaigns" },
          { id: "templates", label: "Email Templates" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? "border-cfc-primary text-cfc-primary"
                : "border-transparent text-cfc-text-muted hover:text-cfc-primary"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {activeTab === "campaigns" ? <CampaignManager /> : <EmailTemplates />}
    </div>
  );
};

export default CampaignManagerWithTemplates;
